import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { awsConfig } from '../config/aws-config';

interface SecureFormField {
  field_id: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'url';
  placeholder?: string | null;
  required?: boolean;
  validation_pattern?: string | null;
}

interface SecureFormConfig {
  formId?: string;
  form_id?: string; // Alternative field name
  title: string;
  description: string;
  fields: SecureFormField[];
}

export interface IndustryFormConfig {
  formId: string;
  title: string;
  description: string;
  industryGroups: string[];
  experienceOptions: number[];
}

export interface WebSocketMessage {
  action: string;
  jobId?: string;
  status?: string;
  message?: string;
  error?: string;
  timestamp?: string;
  attemptCount?: number;
  downloadUrl?: string;
  // Fields for ResumeInterview streaming
  resumeInterviewId?: string;
  streamMessageId?: string;
  token?: string;
  isAutoText?: boolean;
  fullMessage?: string;
  clientRequestId?: string;
  // Fields for direct messages (like welcome message)
  type?: string;
  content?: string;
  // Fields for tool execution
  toolCalls?: Array<{ name: string; id: string }>;
  toolName?: string;
  toolCallId?: string;
  // Fields for document updates
  document?: string;
  updateType?: 'append' | 'edit';
  // Fields for state hierarchy updates
  stateHierarchy?: any;
  // Fields for secure forms
  formConfig?: SecureFormConfig;
  form?: SecureFormConfig; // Alternative field name for backward compatibility
  messageId?: string;
  // Fields for industry selection form
  industryFormConfig?: IndustryFormConfig;
  // Fields for todos
  todo?: any;
  // Fields for project name generation
  projectId?: string;
  fullName?: string;
  // Fields for market insights
  insightCount?: number;
  // Fields for state data passing
  newStateData?: any;
}

export type WebSocketEventHandler = (message: WebSocketMessage) => void;

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
  private isConnecting = false;
  private shouldReconnect = true;
  private connectionState: ConnectionState = 'disconnected';
  private connectionPromise: Promise<void> | null = null;

  async connect(): Promise<void> {
    // If already connected, return immediately
    if (this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    // If already connecting, return the existing promise
    if (this.isConnecting && this.connectionPromise) {
      return this.connectionPromise;
    }

    // Reset shouldReconnect for this new explicit connection attempt
    this.shouldReconnect = true; 
    // Create new connection promise
    this.connectionPromise = this.establishConnection();
    return this.connectionPromise;
  }

  private async establishConnection(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      let readyTimeout: NodeJS.Timeout | null = null;
      let hasReceivedReady = false;
      try {
        this.isConnecting = true;
        this.connectionState = 'connecting';
        this.emit('connectionStateChanged', { action: 'connectionStateChanged', message: 'connecting' });

        const user = await getCurrentUser();
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        // Construct WebSocket URL
        const wsUrl = new URL(awsConfig.websocket.url);
        // Use the sub (subject) from the ID token for consistent user identification
        const idTokenPayload = session.tokens?.idToken?.payload;
        const userSub = idTokenPayload?.sub as string;
        
        if (!userSub) {
          const error = new Error('Failed to extract user ID from authentication token. Please sign in again.');
          console.error('No sub found in ID token payload:', idTokenPayload);
          throw error;
        }
        
        if (!token) {
          const error = new Error('No authentication token available. Please sign in again.');
          throw error;
        }

        // Pass JWT token via Sec-WebSocket-Protocol header
        // Format: "jwt.TOKEN" where TOKEN is the actual JWT
        this.ws = new WebSocket(wsUrl.toString(), [`jwt.${token}`]);

        // Set up connection timeout
        const connectionTimeout = setTimeout(() => {
          if (this.ws?.readyState === WebSocket.CONNECTING) {
            this.ws.close();
            this.connectionState = 'error';
            this.isConnecting = false;
            this.connectionPromise = null;
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000); // 10 second timeout

        this.ws.onopen = () => {
          clearTimeout(connectionTimeout);

          // Send init message to trigger ready response from server
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.send({ action: 'init' });
          }
          
          // Set up ready timeout (5 seconds to receive ready message)
          readyTimeout = setTimeout(() => {
            if (!hasReceivedReady) {
              console.error('Timeout waiting for ready confirmation from server (15s)');
              this.ws?.close();
              this.connectionState = 'error';
              this.isConnecting = false;
              this.connectionPromise = null;
              reject(new Error('Server did not confirm connection readiness (15s timeout)'));
            }
          }, 15000); // Increased timeout from 5s to 15s
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            
            // Add detailed logging for projectName messages
            if (message.action && message.action.includes('projectName')) {
            }
            
            // Handle ready confirmation
            if (message.action === 'ready' && !hasReceivedReady) {
              hasReceivedReady = true;
              if (readyTimeout) {
                clearTimeout(readyTimeout);
                readyTimeout = null;
              }
              this.isConnecting = false;
              this.connectionState = 'connected';
              this.reconnectAttempts = 0;
              this.connectionPromise = null;
              
              this.emit('connected', { action: 'connected' });
              this.emit('connectionStateChanged', { action: 'connectionStateChanged', message: 'connected' });
              resolve();
              return;
            }
            
            // Only process other messages after ready confirmation
            if (hasReceivedReady) {
              this.emit(message.action, message);
              
              // Emit specific job updates
              if (message.jobId) {
                this.emit(`job:${message.jobId}`, message);
              }
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          if (readyTimeout) {
            clearTimeout(readyTimeout);
            readyTimeout = null;
          }
          // More detailed log for onclose
          const targetUrl = event.target instanceof WebSocket ? event.target.url : 'N/A';
          this.isConnecting = false;
          this.connectionState = 'disconnected';
          this.ws = null;
          this.connectionPromise = null;
          
          this.emit('disconnected', { action: 'disconnected' });
          this.emit('connectionStateChanged', { action: 'connectionStateChanged', message: 'disconnected' });

          // Only reject if this was the initial connection attempt
          if (this.reconnectAttempts === 0) {
            reject(new Error(`WebSocket connection closed: ${event.code} ${event.reason}`));
          }

          if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error('WebSocket error:', error);
          this.isConnecting = false;
          this.connectionState = 'error';
          this.connectionPromise = null;
          
          this.emit('error', { action: 'error', error: 'WebSocket connection error' });
          this.emit('connectionStateChanged', { action: 'connectionStateChanged', message: 'error' });
          reject(new Error('WebSocket connection error'));
        };

      } catch (error) {
        console.error('Error establishing WebSocket connection:', error);
        this.isConnecting = false;
        this.connectionState = 'error';
        this.connectionPromise = null;
        
        this.emit('error', { action: 'error', error: 'Failed to connect to WebSocket' });
        this.emit('connectionStateChanged', { action: 'connectionStateChanged', message: 'error' });
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.shouldReconnect = false;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    
    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect();
      }
    }, delay);
  }

  send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message:', message);
    }
  }

  subscribeToJob(jobId: string): void {
    this.send({
      action: 'subscribeToJob',
      jobId,
    });
  }

  getJobStatus(jobId: string): void {
    this.send({
      action: 'getJobStatus',
      jobId,
    });
  }

  ping(): void {
    this.send({
      action: 'ping',
    });
  }

  on(event: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  off(event: string, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, message: WebSocketMessage): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Error in WebSocket event handler:', error);
        }
      });
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  getWebSocketState(): string {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }

  async waitForConnection(): Promise<void> {
    if (this.connectionState === 'connected') {
      return Promise.resolve();
    }
    
    if (this.connectionState === 'connecting' && this.connectionPromise) {
      return this.connectionPromise;
    }
    
    return this.connect();
  }
}

export const webSocketService = new WebSocketService();
